import React from 'react';
import { Link } from 'react-router';

interface AppLinkButtonProps {
  to: string;
  children: React.ReactNode;
  className?: string;
  variant: 'primary' | 'secondary';
  disabled?: boolean;
}

/**
 * A button that's styled like a link, for navigation within the app.
 * @param to The target URL/path.
 * @param children The content inside the link button.
 * @param variant The style variant ('primary' or 'secondary').
 * @param className Additional CSS classes to apply.
 * @param disabled Whether the link button is disabled.
 */
const AppLinkButton: React.FC<AppLinkButtonProps> = ({
  to,
  children,
  variant = 'primary',
  className = '',
  disabled = false
}) => {
  const baseClasses = 'inline-flex items-center justify-center pb-1 text-md font-medium transition-colors hover:underline';

  const disabledClasses = 'opacity-50 cursor-not-allowed pointer-events-none';

  const variantClasses = {
    primary: 'text-blue-600',
    secondary: 'text-gray-500',
  };

  const combinedClasses = `
    ${baseClasses}
    ${variantClasses[variant]}
    ${disabled ? disabledClasses : ''}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  if (disabled) {
    return (
      <span className={combinedClasses}>
        {children}
      </span>
    );
  }

  return (
    <Link to={to} className={combinedClasses}>
      {children}
    </Link>
  );
};

export default AppLinkButton;
